/**
 * Shared number formatters — the only place a number becomes text.
 * DESIGN.md › The One Money Voice Rule / The Column Rule.
 */
const MINUS = '−';
const safe = (v: number) => (Number.isFinite(v) ? v : 0);
/** Whole units, halves away from zero (−2.5 → −3), as the emails' fmt_money
 *  and fmtK round; Math.round alone sends −2.5 to −2 (math audit 2026-10-13). */
const whole = (v: number) => Math.sign(v) * Math.round(Math.abs(v)) || 0;
const oneDecimal = (v: number) => {
  const r = Math.round(Math.abs(v) * 10) / 10;
  return Number.isInteger(r) ? r.toString() : r.toFixed(1);
};
const signed = (v: number) => {
  const safe_v = safe(v);
  const rounded_mag = Math.round(Math.abs(safe_v) * 10) / 10;
  if (rounded_mag <= 0) return '';
  return safe_v > 0 ? '+' : MINUS;
};

/** Totals over a period: whole dollars. $36,815 · −$1,234 */
export const fmt = (v: number): string => {
  const r = whole(safe(v));
  return `${r < 0 ? MINUS : ''}$${Math.abs(r).toLocaleString('en-US')}`;
};

/** A money change versus before: +$1,234 · −$1,234 · $0 (no sign on a zero). */
export const fmtSigned = (v: number): string => {
  const r = whole(safe(v));
  return r > 0 ? `+${fmt(r)}` : fmt(r);
};

/** Which way a money change points, on the whole dollars fmtSigned prints —
 *  so a tone or tint keyed to it can never disagree with the printed sign. */
export const moneyDirection = (v: number): 'up' | 'down' | 'flat' => {
  const r = whole(safe(v));
  return r === 0 ? 'flat' : r > 0 ? 'up' : 'down';
};

/** One price, unit cost, payment, invoice, or a total the owner checks against the register. $4.89 */
export const fmtCents = (v: number): string => {
  const r = Math.round(safe(v) * 100) / 100;
  return `${r < 0 ? MINUS : ''}$${Math.abs(r).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Chart axes: $950 · $37K · $4.5K · $1.2M · $2.5B. Past $999B it caps at
 *  "$999B+" rather than print a run of digits (math audit row 32: fmtK(1e15)
 *  printed "$1000000000M"). */
const K_TIERS: [number, string][] = [[1e3, 'K'], [1e6, 'M'], [1e9, 'B']];
export const fmtK = (v: number): string => {
  const n = safe(v);
  const rounded = Math.round(Math.abs(n));
  const s = n < 0 && rounded > 0 ? MINUS : '';
  if (rounded < 1_000) return `${s}$${rounded}`;
  for (let i = 0; i < K_TIERS.length; i++) {
    const [size, unit] = K_TIERS[i];
    const next = i + 1 < K_TIERS.length ? K_TIERS[i + 1][0] : Infinity;
    if (rounded >= next) continue;
    const scaled = rounded / size;
    const shown = scaled >= 10 ? Math.round(scaled) : Math.round(scaled * 10) / 10;
    if (shown >= 1_000) continue; // rounds to 1000 of this unit: the next unit says it
    return `${s}$${scaled >= 10 ? shown.toString() : oneDecimal(scaled)}${unit}`;
  }
  return `${s}$999B+`;
};

/** Counts: 1,204 */
export const fmtCount = (v: number): string => {
  const r = whole(safe(v));
  return `${r < 0 ? MINUS : ''}${Math.abs(r).toLocaleString('en-US')}`;
};

/** A percent's magnitude, one decimal, with thousands separators; past
 *  9,999 it caps (math audit row 32) and `capped` adds a "+". `fixed` keeps
 *  the decimal on whole numbers too (4.0, 9,999.0), for a column of them. */
const PCT_CAP = 9_999;
const pctBody = (v: number, fixed = false): { text: string; capped: boolean } => {
  const r = Math.round(Math.abs(safe(v)) * 10) / 10;
  const digits = fixed || !Number.isInteger(Math.min(r, PCT_CAP)) ? 1 : 0;
  const text = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: 1 });
  if (r > PCT_CAP) return { text: text(PCT_CAP), capped: true };
  return { text: text(r), capped: false };
};

/** A share of a whole: 31.4% · 1,234.5% · 9,999%+ */
export const fmtPct = (v: number): string => {
  const safe_v = safe(v);
  const rounded_magnitude = Math.round(Math.abs(safe_v) * 10) / 10;
  const sign = safe_v < 0 && rounded_magnitude > 0 ? MINUS : '';
  const { text, capped } = pctBody(safe_v);
  return `${sign}${text}%${capped ? '+' : ''}`;
};

/** Change versus before: +4.2% · −3.1% · 0% · +9,999%+ */
export const fmtChange = (v: number): string => {
  const { text, capped } = pctBody(v);
  return `${signed(v)}${text}%${capped ? '+' : ''}`;
};

/** A change that may have no base: null when the earlier period was $0
 *  (math audit row 6), where no percent is honest. "new" when something now
 *  sells against nothing, an empty string otherwise. */
export const fmtChangeOrNew = (v: number | null | undefined, current: number): string =>
  v == null ? (safe(current) > 0 ? 'new' : '') : fmtChange(v);

/** Change in a percentage (e.g. margin): +2.1 pts · +9,999+ pts */
export const fmtPts = (v: number): string => {
  const { text, capped } = pctBody(v);
  return `${signed(v)}${text}${capped ? '+' : ''} pts`;
};

/** Change versus before with a fixed decimal, for a column of them: +4.0% · −15.1% · 0.0% · +1,500.0% · +9,999.0%+ */
export const fmtChangeFixed = (v: number): string => {
  const { text, capped } = pctBody(v, true);
  return `${signed(v)}${text}%${capped ? '+' : ''}`;
};

export const changeDirection = (v: number): 'up' | 'down' | 'flat' => {
  const safe_v = safe(v);
  const rounded_mag = Math.round(Math.abs(safe_v) * 10) / 10;
  if (rounded_mag === 0) return 'flat';
  return safe_v > 0 ? 'up' : 'down';
};
